import { createServerFn } from "@tanstack/react-start";
import crypto from "node:crypto";

import { requireSupabaseAuth } from "@/lib/require-auth";

export type HealthStatus = "ok" | "degraded" | "down" | "not_configured";

export interface IntegrationCheck {
  id: string;
  name: string;
  status: HealthStatus;
  message: string;
  lastCheckedAt: string;
  elapsedMs: number;
  details: Record<string, string | number | boolean | null>;
}

export interface IntegrationsHealth {
  generatedAt: string;
  overall: HealthStatus;
  checks: IntegrationCheck[];
  bokunCache: {
    entries: number;
    freshEntries: number;
    staleEntries: number;
    expiredEntries: number;
    maxEntries: number;
    oldestExpiresAt: string | null;
    newestExpiresAt: string | null;
    hitRate: number;
    hits: number;
    staleHits: number;
    misses: number;
    expirations: number;
    evictions: number;
    writes: number;
    revalidations: number;
    revalErrors: number;
    rtt: {
      samples: number;
      avgMs: number;
      p50Ms: number;
      p95Ms: number;
      maxMs: number;
      slowCount: number;
    };
    slowestPaths: Array<{ path: string; count: number; avgMs: number; maxMs: number; slowCount: number }>;
  };
  bokunMetrics: {
    requests: number;
    attempts: number;
    retries: number;
    rateLimited: number;
    serverErrors: number;
    networkErrors: number;
    failures: number;
  };
  lastBokunAttempts: Array<{
    ts: string;
    method: string;
    path: string;
    status: number | null;
    outcome: "ok" | "retry" | "error";
    elapsedMs: number;
    reason?: string;
  }>;
  lastBokunSuccessAt: string | null;
}

async function ensureAdmin(userId: string, supabase: any) {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .in("role", ["admin", "owner"])
    .limit(1);
  if (error) throw new Error(error.message);
  if (!data || data.length === 0) throw new Error("No tienes permisos de administrador.");
}

function bokunSignedHeaders(accessKey: string, secretKey: string, method: string, path: string) {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  const date = `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())}`;
  const signature = crypto.createHmac("sha1", secretKey).update(date + accessKey + method + path).digest("base64");
  return {
    "X-Bokun-Date": date,
    "X-Bokun-AccessKey": accessKey,
    "X-Bokun-Signature": signature,
    "Content-Type": "application/json",
    Accept: "application/json",
  };
}

async function checkBokun(): Promise<{ check: IntegrationCheck; lastSuccessAt: string | null }> {
  const startedAt = Date.now();
  const accessKey = process.env.BOKUN_ACCESS_KEY ?? "";
  const secretKey = process.env.BOKUN_SECRET_KEY ?? "";
  const base = (process.env.BOKUN_API_URL || "https://api.bokun.io").replace(/\/+$/, "");
  const path = "/activity.json/search";
  const now = new Date().toISOString();

  if (!accessKey || !secretKey) {
    return {
      check: {
        id: "bokun",
        name: "Bokun (REST Legacy v1)",
        status: "not_configured",
        message: "BOKUN_ACCESS_KEY o BOKUN_SECRET_KEY no están configurados.",
        lastCheckedAt: now,
        elapsedMs: Date.now() - startedAt,
        details: { baseUrl: base, endpoint: `POST ${base}${path}` },
      },
      lastSuccessAt: null,
    };
  }

  try {
    const res = await fetch(`${base}${path}`, {
      method: "POST",
      headers: bokunSignedHeaders(accessKey, secretKey, "POST", path),
      body: JSON.stringify({ forSale: true, pageSize: 1, page: 1 }),
    });
    const elapsedMs = Date.now() - startedAt;
    if (!res.ok) {
      return {
        check: {
          id: "bokun",
          name: "Bokun (REST Legacy v1)",
          status: "down",
          message: `HTTP ${res.status} ${res.statusText}`,
          lastCheckedAt: now,
          elapsedMs,
          details: { baseUrl: base, endpoint: `POST ${base}${path}`, httpStatus: res.status },
        },
        lastSuccessAt: null,
      };
    }
    const json = await res.json().catch(() => null);
    const items = Array.isArray(json)
      ? json
      : Array.isArray(json?.items)
        ? json.items
        : Array.isArray(json?.results)
          ? json.results
          : [];
    return {
      check: {
        id: "bokun",
        name: "Bokun (REST Legacy v1)",
        status: "ok",
        message: `Conectado. ${items.length} producto(s) en la respuesta de prueba.`,
        lastCheckedAt: now,
        elapsedMs,
        details: { baseUrl: base, endpoint: `POST ${base}${path}`, httpStatus: res.status, sampleProducts: items.length },
      },
      lastSuccessAt: now,
    };
  } catch (err) {
    return {
      check: {
        id: "bokun",
        name: "Bokun (REST Legacy v1)",
        status: "down",
        message: `Error de red: ${(err as Error).message}`,
        lastCheckedAt: now,
        elapsedMs: Date.now() - startedAt,
        details: { baseUrl: base },
      },
      lastSuccessAt: null,
    };
  }
}

async function checkSupabase(supabase: any): Promise<IntegrationCheck> {
  const startedAt = Date.now();
  const now = new Date().toISOString();
  try {
    const { error } = await supabase.from("user_roles").select("user_id", { count: "exact", head: true }).limit(1);
    const elapsedMs = Date.now() - startedAt;
    if (error) {
      return {
        id: "supabase",
        name: "Lovable Cloud (DB)",
        status: "down",
        message: error.message,
        lastCheckedAt: now,
        elapsedMs,
        details: {},
      };
    }
    return {
      id: "supabase",
      name: "Lovable Cloud (DB)",
      status: "ok",
      message: "Conexión a la base de datos OK.",
      lastCheckedAt: now,
      elapsedMs,
      details: {},
    };
  } catch (err) {
    return {
      id: "supabase",
      name: "Lovable Cloud (DB)",
      status: "down",
      message: (err as Error).message,
      lastCheckedAt: now,
      elapsedMs: Date.now() - startedAt,
      details: {},
    };
  }
}

function checkConfigured(id: string, name: string, envVar: string, extra: Record<string, string | number | boolean | null> = {}): IntegrationCheck {
  const now = new Date().toISOString();
  const v = process.env[envVar];
  return {
    id,
    name,
    status: v ? "ok" : "not_configured",
    message: v ? "Clave configurada." : `${envVar} no está configurada.`,
    lastCheckedAt: now,
    elapsedMs: 0,
    details: { ...extra, configured: !!v },
  };
}

async function checkLovableAI(): Promise<IntegrationCheck> {
  const startedAt = Date.now();
  const now = new Date().toISOString();
  const key = process.env.LOVABLE_API_KEY;
  if (!key) {
    return {
      id: "lovable_ai",
      name: "Lovable AI Gateway",
      status: "not_configured",
      message: "LOVABLE_API_KEY no está configurada.",
      lastCheckedAt: now,
      elapsedMs: 0,
      details: {},
    };
  }
  try {
    // El gateway no expone /v1/models. Validamos con una llamada real mínima
    // a /v1/chat/completions (mismo endpoint que usan Maya, traducciones, etc.)
    // con el header oficial `Lovable-API-Key` y max_tokens=1 para minimizar costo.
    const res = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Lovable-API-Key": key,
      },
      body: JSON.stringify({
        model: "google/gemini-3-flash-preview",
        messages: [{ role: "user", content: "ping" }],
        max_tokens: 1,
      }),
    });
    const elapsedMs = Date.now() - startedAt;
    // 200 OK => sano. 402 (créditos) y 429 (rate limit) => degradado pero el gateway responde.
    const status: IntegrationCheck["status"] = res.ok
      ? "ok"
      : res.status === 402 || res.status === 429
        ? "degraded"
        : "down";
    return {
      id: "lovable_ai",
      name: "Lovable AI Gateway",
      status,
      message: res.ok ? "Gateway responde." : `HTTP ${res.status} ${res.statusText}`,
      lastCheckedAt: now,
      elapsedMs,
      details: { httpStatus: res.status, endpoint: "/v1/chat/completions" },
    };

  } catch (err) {
    return {
      id: "lovable_ai",
      name: "Lovable AI Gateway",
      status: "down",
      message: (err as Error).message,
      lastCheckedAt: now,
      elapsedMs: Date.now() - startedAt,
      details: {},
    };
  }
}

function rollupStatus(checks: IntegrationCheck[]): HealthStatus {
  if (checks.some((c) => c.status === "down")) return "down";
  if (checks.some((c) => c.status === "degraded")) return "degraded";
  if (checks.every((c) => c.status === "not_configured")) return "not_configured";
  return "ok";
}

export const getIntegrationsHealth = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<IntegrationsHealth> => {
    await ensureAdmin(context.userId, context.supabase);

    // Lazy-load server-only Bokun core (keep out of client bundle).
    const { getBokunCacheStats, getBokunMetrics } = await import("./bokun-core.server");

    const [bokun, supabase, lovableAi] = await Promise.all([
      checkBokun(),
      checkSupabase(context.supabase),
      checkLovableAI(),
    ]);

    const stripe = checkConfigured("stripe", "Stripe (pagos)", "STRIPE_LIVE_API_KEY", {
      webhookConfigured: !!process.env.PAYMENTS_LIVE_WEBHOOK_SECRET,
    });
    const travelpayouts = checkConfigured(
      "travelpayouts",
      "TravelPayouts (vuelos)",
      "TRAVELPAYOUTS_API_TOKEN",
      { markerConfigured: !!process.env.TRAVELPAYOUTS_MARKER },
    );

    const now = new Date().toISOString();
    const waMissing: string[] = [];
    if (!process.env.TWILIO_API_KEY) waMissing.push("TWILIO_API_KEY (conexión de Twilio)");
    if (!process.env.WHATSAPP_FROM) waMissing.push("WHATSAPP_FROM (número remitente)");
    const whatsapp: IntegrationCheck = {
      id: "whatsapp",
      name: "WhatsApp (Twilio)",
      status: waMissing.length === 0 ? "ok" : "not_configured",
      message:
        waMissing.length === 0
          ? "Credenciales presentes: los envíos automáticos de WhatsApp están habilitados."
          : `Falta configurar: ${waMissing.join(", ")}. Los mensajes de WhatsApp no se envían hasta configurarlo.`,
      lastCheckedAt: now,
      elapsedMs: 0,
      details: {
        twilioConnected: !!process.env.TWILIO_API_KEY,
        senderConfigured: !!process.env.WHATSAPP_FROM,
        gatewayKey: !!process.env.LOVABLE_API_KEY,
      },
    };

    const email: IntegrationCheck = {
      id: "email",
      name: "Correo transaccional",
      status: "ok",
      message: "Dominio notify.mostrandomexico.mx verificado; cola de envío activa.",
      lastCheckedAt: now,
      elapsedMs: 0,
      details: { senderDomain: "notify.mostrandomexico.mx" },
    };

    const cacheStats = getBokunCacheStats();
    const metrics = getBokunMetrics();
    const recent = metrics.attempts.slice(-20).reverse();
    const lastSuccess = [...metrics.attempts].reverse().find((a) => a.outcome === "ok");
    const lastBokunSuccessAt = bokun.lastSuccessAt ?? lastSuccess?.ts ?? null;

    const checks: IntegrationCheck[] = [bokun.check, supabase, stripe, lovableAi, travelpayouts, email, whatsapp];

    return {
      generatedAt: new Date().toISOString(),
      overall: rollupStatus(checks),
      checks,
      bokunCache: {
        entries: cacheStats.entries,
        freshEntries: cacheStats.freshEntries,
        staleEntries: cacheStats.staleEntries,
        expiredEntries: cacheStats.expiredEntries,
        maxEntries: cacheStats.maxEntries,
        oldestExpiresAt: cacheStats.oldestExpiresAtMs ? new Date(cacheStats.oldestExpiresAtMs).toISOString() : null,
        newestExpiresAt: cacheStats.newestExpiresAtMs ? new Date(cacheStats.newestExpiresAtMs).toISOString() : null,
        hitRate: cacheStats.hitRate,
        hits: cacheStats.totals.hits,
        staleHits: cacheStats.totals.staleHits,
        misses: cacheStats.totals.misses,
        expirations: cacheStats.totals.expirations,
        evictions: cacheStats.totals.evictions,
        writes: cacheStats.totals.writes,
        revalidations: cacheStats.totals.revalidations,
        revalErrors: cacheStats.totals.revalErrors,
        rtt: cacheStats.rtt,
        slowestPaths: cacheStats.slowestPaths,
      },
      bokunMetrics: metrics.totals,
      lastBokunAttempts: recent.map((a) => ({
        ts: a.ts,
        method: a.method,
        path: a.path,
        status: a.status,
        outcome: a.outcome,
        elapsedMs: a.elapsedMs,
        reason: a.reason,
      })),
      lastBokunSuccessAt,
    };
  });

export const clearBokunCacheFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureAdmin(context.userId, context.supabase);
    const { clearBokunCache } = await import("./bokun-core.server");
    clearBokunCache();
    return { ok: true, clearedAt: new Date().toISOString() };
  });
